import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ReorderlevelStatusBadge from "components/StatusBadge/ReorderlevelStatusBadge";
import { app_modules } from "helpers/apps_modules_list";
import React, { useCallback, useEffect, useMemo } from "react";
import { Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import usePermission from "CustomHooks/usePermission";

import { Axios } from "api/axios";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import { api } from "helpers/api_url";
import { showConfirm, showError, showSuccess } from "utils/swalHelper";
import EditModal from "./EditModal";

import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import { fetchreorderlevel } from "slices/Inventory/ReorderLevel/ItemWise/thunk";

import { ColumnFiltersState, SortingState } from "@tanstack/react-table";

// =============================
// INTERFACE
// =============================
interface ItemWiseprop {
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  filterValue: any[];
  search: string;
  sortField: string;
  sortDirection: "asc" | "desc" | "";

  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  setFilterValue: (filters: any[]) => void;
}

const StockOverView: React.FC<ItemWiseprop> = ({
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  sortField,
  sortDirection,

  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
}) => {
  document.title = "Item Wise ";

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const [showEditModal, setShowEditModal] = React.useState(false);
  const [editRow, setEditRow] = React.useState<any | null>(null);
  const [editableValue, setEditableValue] = React.useState<string>("");
  const [isSaving, setIsSaving] = React.useState(false);

  const openEditModal = (row: any) => {
    setEditRow(row);
    // support both possible field names
    const val = row?.record_level ?? row?.reorder_level ?? "";
    setEditableValue(String(val));
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditRow(null);
    setEditableValue("");
  };

  const handleUpdate = async () => {
    if (!editRow) return;
    try {
      setIsSaving(true);
      const id = editRow?.reorder_level_id || editRow?.id;
      if (!id) {
        showError("Cannot determine record id", "Update Failed");
        setIsSaving(false);
        return;
      }

      const payload = {
        // send reorder_level as backend naming suggests
        reorder_level: editableValue,
      };

      await Axios.put(
        `${api?.Inventory?.Reorderlevel?.ItemWise?.edit}/${id}`,
        payload
      );
      await showSuccess("Record updated successfully", "Updated");
      closeEditModal();

      // Refresh list
      const payloadFetch = {
        page: pageIndex + 1,
        pageSize,
        search,
        sort: sortField,
        order: sortDirection,
        filters: filterValue,
      };
      dispatch(fetchreorderlevel(payloadFetch));
    } catch (error: any) {
      console.error("Update error:", error);
      showError(error?.message || "Failed to update record", "Update Failed");
    } finally {
      setIsSaving(false);
    }
  };

  // ================================
  // SELECTOR (Same structure as InStock)
  // ================================
  const selectStockOverview = createSelector(
    (state: any) => state.recordLevelIteamWiseReducer,
    (item) => ({
      list: item?.list || [],
      rowCount: item?.rowCount || 0,
      loading: item?.loading,
      error: item?.error,
    })
  );

  const { list, rowCount, loading, error } = useSelector(selectStockOverview);

  // Use custom hook to fetch and access permissions
  const { canView, canUpdate} = usePermission(
    app_modules?.inventory?.app_name,
    app_modules?.inventory?.modules?.reorder_level
  );

  // ================================
  // FETCH API (Same pattern as InStock)
  // ================================
  useEffect(() => {
    const payload = {
      page: pageIndex + 1,
      pageSize,
      search,
      sort: sortField,
      order: sortDirection,
      filters: filterValue,
    };

    dispatch(fetchreorderlevel(payload));
  }, [
    dispatch,
    pageIndex,
    pageSize,
    search,
    sortField,
    sortDirection,
    filterValue,
  ]);

  const handleDelete = useCallback(
    async (reorder_level_id: string) => {
      try {
        const result = await showConfirm(
          "This will permanently delete the record. Do you want to continue?",
          "Confirm Deletion",
          "Delete",
          "Cancel"
        );
        if (!result?.isConfirmed) return;

        const url = `${api?.Inventory?.Reorderlevel?.ItemWise?.delete}/${reorder_level_id}`;
        await Axios.delete(url);
        await showSuccess("Record deleted successfully", "Deleted");

        // Refresh list after deletion
        const payload = {
          page: pageIndex + 1,
          pageSize,
          search,
          sort: sortField,
          order: sortDirection,
          filters: filterValue,
        };
        dispatch(fetchreorderlevel(payload));
      } catch (error: any) {
        console.error("Delete error:", error);
        showError(error?.message || "Failed to delete record", "Delete Failed");
      }
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      search,
      sortField,
      sortDirection,
      filterValue,
    ]
  );


  // ================================
  // ACTION COLUMN
  // ================================
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
        permission: (row: any) => {
          return !!(
            canUpdate &&
            row?.dispatch_status?.toLowerCase() !== "delivered"
          );
        },
        onClick: (row: any) => {
          openEditModal(row);
        },
        color: "primary",
      },
      {
        icon: DeleteIcon,
        label: "Delete",
        permission: (row: any) => {
          return !!(
            canUpdate &&
            row?.dispatch_status?.toLowerCase() !== "delivered"
          );
        },
        onClick: (row: any) => {
          handleDelete(row.reorder_level_id);
        },
        color: "info",
      },
    ];
    return items;
  }, [navigate, handleDelete, canUpdate]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    if (!canUpdate) return [];

    return [
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [menuItems, canUpdate]);

  // ================================
  // TABLE COLUMNS (Same style as InStock)
  // ================================
  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return row.original.sr_no || pageIndex * pageSize + row.index + 1;
        },
      },
      {
        header: "Item Name",
        accessorKey: "item_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.Inventory.Reorderlevel.ItemWise.dropdown}/item_name`,
        cell: ({ row }: { row: any }) => row.original?.item_name || "-",
      },
      {
        header: "Item Code",
        accessorKey: "item_code",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.Inventory.Reorderlevel.ItemWise.dropdown}/item_code`,
        cell: ({ row }: { row: any }) => row.original?.item_code || "-",
      },

      {
        header: "Category",
        accessorKey: "category_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api.Inventory.Reorderlevel.ItemWise.dropdown}/category_name`,
      },

      {
        header: "Sub Category",
        accessorKey: "sub_category_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api.Inventory.Reorderlevel.ItemWise.dropdown}/sub_category_name`,
      },

      {
        header: "Unit",
        accessorKey: "unit_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api.Inventory.Reorderlevel.ItemWise.dropdown}/unit_name`,
      },

      {
        header: "Total Stock",
        accessorKey: "available_stock",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Reorder level",
        accessorKey: "reorder_level",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableSorting: true,
        enableColumnFilter: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.Inventory.Reorderlevel.ItemWise.dropdown}/status`,
        cell: ({ row }: { row: any }) => {
          return (
            <ReorderlevelStatusBadge status={row?.original?.status || "-"} />
          );
        },
      },
      {
        header: "Created By / Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username || "-";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By / Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username || "-";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [list, DynamicAction]
  );

  useEffect(() => {
    console.log("Redux reorder level state:", list, rowCount, loading);
  }, [list, rowCount, loading]);
  // ================================
  // RENDER
  // ================================
  return (
    <Card className="p-3">
      <LoaderErrorWrapper loading={loading} error={error}>
        <TableContainer2
          columns={columns}
          data={list}
          totalRecords={rowCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPageChange={setPageIndex}
          onPageSizeChange={setPageSize}
          sorting={sorting}
          onSortingChange={setSorting}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          colValue={setFilterValue}
          isPagination={true}
          manualSorting={true}
          isGlobalFilter={true}
          SearchPlaceholder="Search Items..."
        />
      </LoaderErrorWrapper>

      <EditModal
        show={showEditModal}
        editRow={editRow}
        editableValue={editableValue}
        onEditableValueChange={setEditableValue}
        onSave={handleUpdate}
        onClose={closeEditModal}
        isSaving={isSaving}
      />
    </Card>
  );
};

export default StockOverView;
